import React, { useState } from 'react';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import { AlertTriangle } from 'lucide-react';
import { useClientStore } from '@/store/clientStore';
import toast from 'react-hot-toast';

interface MissingBusinessDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  clientId: string;
  clientName: string;
  missingFields?: {
    address?: boolean;
    gst?: boolean;
  };
}

export const MissingBusinessDetailsModal: React.FC<MissingBusinessDetailsModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  clientId,
  clientName,
  missingFields = { address: true, gst: true }
}) => {
  const [address, setAddress] = useState('');
  const [gst, setGst] = useState('');
  const [loading, setLoading] = useState(false);
  const { updateClient } = useClientStore();

  const handleSave = async () => {
    if (missingFields.address && !address.trim()) {
      toast.error('Please enter the address');
      return;
    }
    if (missingFields.gst && !gst.trim()) {
      toast.error('Please enter the GST number');
      return;
    }

    setLoading(true);
    try {
      await updateClient(clientId, { address, gst });
      toast.success('Business details updated successfully');
      onComplete();
    } catch (error) {
      toast.error('Failed to update business details');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={`Missing Business Details for ${clientName}`}
      size="md"
    >
      <div className="space-y-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">
                Missing Business Details
              </h3>
              <p className="text-sm text-yellow-700 mt-1">
                To generate this invoice, we need a few more details about the business. 
                Please complete the missing info.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {missingFields.address && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <textarea
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="4th Floor, Venus Atlantis, Prahladnagar, Ahmedabad"
              />
            </div>
          )}

          {missingFields.gst && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GST Number
                <span className="text-xs text-gray-500 ml-1">(optional if not registered)</span>
              </label>
              <input
                type="text"
                value={gst}
                onChange={(e) => setGst(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="24DDDTP7890P1ZV"
              />
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} loading={loading}>
            Save & Continue
          </Button>
        </div>
      </div>
    </Modal>
  );
};